<template>
  <Card>
    <template #title>
      <div class="text-2xl">Step 1: Group Details</div>
    </template>
    <template #content>
      <!-- Toggle: New Group / Existing Group -->
      <div class="mb-4">
        <label class="block mb-2 font-semibold">Group Mode:</label>
        <SelectButton
          v-model="groupMode"
          :options="groupModeOptions"
          optionLabel="label"
          optionValue="value"
        />
      </div>

      <!-- NEW GROUP FORM -->
      <div v-if="groupMode === 'new'" class="mt-4">
        <!-- Partner Selection -->
        <div class="mb-4 p-3 surface-100 border-round">
          <label class="block mb-2 font-semibold">*Partner</label>
          <Dropdown
            v-model="selectedPartnerId"
            :options="partners"
            optionLabel="name"
            optionValue="id"
            placeholder="Select Partner"
            filter
            :loading="loadingPartners"
            class="w-full"
            :class="{ 'p-invalid': submitted && hasError('partner_id') }"
          />
          <small v-if="submitted && hasError('partner_id')" class="p-error">
            {{ getError("partner_id") }}
          </small>
          <small class="block mt-1 text-500">
            Select the Partner this group belongs to
          </small>
        </div>

        <div class="grid">
          <!-- Group Name -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label for="groupName">*Group Name</label>
            <InputText
              id="groupName"
              v-model="form.name"
              :class="{ 'p-invalid': submitted && hasError('name') }"
              placeholder="e.g. Example Media"
            />
            <small v-if="submitted && hasError('name')" class="p-error">
              {{ getError("name") }}
            </small>
          </div>

          <!-- Email -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label for="email">*Email</label>
            <InputText
              id="email"
              v-model="form.email"
              type="email"
              :class="{ 'p-invalid': submitted && hasError('email') }"
              placeholder="e.g. group@example.com"
            />
            <small v-if="submitted && hasError('email')" class="p-error">
              {{ getError("email") }}
            </small>
          </div>

          <!-- Password -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label for="password">*Password</label>
            <InputText
              id="password"
              v-model="form.password"
              type="password"
              :class="{ 'p-invalid': submitted && hasError('password') }"
              placeholder="Min 8 chars with uppercase"
            />
            <small v-if="submitted && hasError('password')" class="p-error">
              {{ getError("password") }}
            </small>
          </div>

          <!-- Company Name -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label for="companyName">*Company Name</label>
            <InputText
              id="companyName"
              v-model="form.company_name"
              :class="{ 'p-invalid': submitted && hasError('company_name') }"
              placeholder="Company Name"
            />
            <small v-if="submitted && hasError('company_name')" class="p-error">
              {{ getError("company_name") }}
            </small>
          </div>

          <!-- Company Domain -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label for="companyDomain">*Company Domain</label>
            <InputText
              id="companyDomain"
              v-model="form.company_domain"
              :class="{ 'p-invalid': submitted && hasError('company_domain') }"
              placeholder="example.com (without www)"
            />
            <small
              v-if="submitted && hasError('company_domain')"
              class="p-error"
            >
              {{ getError("company_domain") }}
            </small>
          </div>

          <!-- Seller Type -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label for="sellerType">*Group Type</label>
            <Dropdown
              id="sellerType"
              v-model="form.seller_type"
              :options="sellerTypes"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Type"
              :class="{ 'p-invalid': submitted && hasError('seller_type') }"
            />
            <small v-if="submitted && hasError('seller_type')" class="p-error">
              {{ getError("seller_type") }}
            </small>
          </div>

          <!-- Contact Person Name -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label for="contactName">*Contact Person Name</label>
            <InputText
              id="contactName"
              v-model="form.contact_person_name"
              :class="{
                'p-invalid': submitted && hasError('contact_person_name'),
              }"
              placeholder="Contact Person Name"
            />
            <small
              v-if="submitted && hasError('contact_person_name')"
              class="p-error"
            >
              {{ getError("contact_person_name") }}
            </small>
          </div>

          <!-- Contact Person Email -->
          <div class="flex flex-column gap-1 col-12 md:col-6">
            <label for="contactEmail">*Contact Person Email</label>
            <InputText
              id="contactEmail"
              v-model="form.contact_person_email"
              type="email"
              :class="{
                'p-invalid': submitted && hasError('contact_person_email'),
              }"
              placeholder="contact@example.com"
            />
            <small
              v-if="submitted && hasError('contact_person_email')"
              class="p-error"
            >
              {{ getError("contact_person_email") }}
            </small>
          </div>

          <!-- Video Monetization Settings -->
          <div class="col-12">
            <Divider />
            <h4 class="mt-3 mb-3">Video Monetization (Optional)</h4>
          </div>

          <!-- Enable Video Monetization -->
          <div class="flex flex-column gap-2 col-12 md:col-6">
            <div class="flex align-items-center">
              <Checkbox
                id="videoMonetization"
                v-model="form.video_monetization_enabled"
                :binary="true"
              />
              <label for="videoMonetization" class="ml-2">
                Enable Video Monetization
              </label>
            </div>
            <small class="text-500">
              Allow sellers to monetize video content
            </small>
          </div>

          <!-- Enable Connatix Reporting -->
          <div class="flex flex-column gap-2 col-12 md:col-6">
            <div class="flex align-items-center">
              <Checkbox
                id="connatixReporting"
                v-model="form.connatix_reporting_enabled"
                :binary="true"
              />
              <label for="connatixReporting" class="ml-2">
                Enable Connatix Reporting
              </label>
            </div>
            <small class="text-500">
              Enable Connatix analytics and reporting
            </small>
          </div>

          <!-- Info Message -->
          <div
            v-if="
              form.video_monetization_enabled || form.connatix_reporting_enabled
            "
            class="col-12"
          >
            <Message severity="info" :closable="false">
              <template #default>
                <strong>Note:</strong> Both flags must be enabled for sellers to
                use "Audio + Video" type.
                <ul class="mt-2 mb-0 pl-1">
                  <li
                    v-if="
                      form.video_monetization_enabled &&
                      form.connatix_reporting_enabled
                    "
                  >
                    Sellers will be able to create "Audio + Video" type
                  </li>
                  <li v-else>
                    Enable both flags to allow "Audio + Video" sellers
                  </li>
                </ul>
              </template>
            </Message>
          </div>
        </div>
      </div>

      <!-- EXISTING GROUP DROPDOWN -->
      <div v-else class="mt-4">
        <div class="flex flex-column gap-2">
          <label for="existingGroup">Select Existing Group:</label>
          <Dropdown
            id="existingGroup"
            v-model="selectedGroup"
            :options="existingGroups"
            optionLabel="name"
            placeholder="Select a group"
            :loading="loadingGroups"
            :class="{ 'p-invalid': submitted && hasError('selectedGroup') }"
            class="w-full md:w-20rem"
          >
            <template #value="slotProps">
              <div v-if="slotProps.value">
                {{ slotProps.value.name }} (ID:
                {{ slotProps.value.display_id }})
              </div>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <div>
                <div class="font-bold">{{ slotProps.option.name }}</div>
                <div class="text-sm text-500">
                  ID: {{ slotProps.option.display_id }}
                </div>
              </div>
            </template>
          </Dropdown>
          <small v-if="submitted && hasError('selectedGroup')" class="p-error">
            {{ getError("selectedGroup") }}
          </small>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-content-between gap-2">
        <Button label="Cancel" outlined @click="$emit('cancel')" />
        <Button
          label="Next"
          icon="pi pi-arrow-right"
          iconPos="right"
          @click="handleNext"
        />
      </div>
    </template>
  </Card>
</template>

<script setup>
import { ref, onMounted, defineEmits, defineProps, watch } from "vue";
import { useToast } from "primevue/usetoast";
import axios from "axios";
import { API_BASE_URL } from "@/consts";
import { useStepValidation } from "./composables/useStepValidation";
import Card from "primevue/card";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import SelectButton from "primevue/selectbutton";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Divider from "primevue/divider";
import Message from "primevue/message";

const toast = useToast();

const emit = defineEmits(["next", "cancel"]);
const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  mode: {
    type: String,
    default: "new",
  },
});

const groupMode = ref("new");
const submitted = ref(false);
const loadingGroups = ref(false);
const loadingPartners = ref(false);
const existingGroups = ref([]);
const selectedGroup = ref(null);
const partners = ref([]);
const selectedPartnerId = ref(null);

const groupModeOptions = [
  { label: "Create New Group", value: "new" },
  { label: "Use Existing Group", value: "existing" },
];

const sellerTypes = [
  { label: "Publisher", value: "PUBLISHER" },
  { label: "Intermediary", value: "INTERMEDIARY" },
  { label: "Both", value: "BOTH" },
];

const defaultForm = {
  name: "",
  email: "",
  password: "",
  company_name: "",
  company_domain: "",
  seller_type: "PUBLISHER",
  contact_person_name: "",
  contact_person_email: "",
  video_monetization_enabled: false, // Default: disabled
  connatix_reporting_enabled: false, // Default: disabled
};

const form = ref({ ...defaultForm });

const hydrateFromProps = () => {
  groupMode.value = props.mode || "new";

  if (!props.data) {
    return;
  }

  if (groupMode.value === "existing") {
    selectedGroup.value = props.data;
  } else {
    form.value = {
      ...defaultForm,
      ...props.data,
    };
  }
};

// Use validation composable (without passing data)
const { errors, validate, hasError, getError } = useStepValidation(1);

// Fetch partners
const fetchPartners = async () => {
  try {
    loadingPartners.value = true;
    const token = localStorage.getItem("adminToken");
    const response = await axios.get(`${API_BASE_URL}/partners`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data) {
      partners.value = response.data;
    }
  } catch (error) {
    console.error("Error fetching partners:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to load partners",
      life: 3000,
    });
  } finally {
    loadingPartners.value = false;
  }
};

// Fetch existing groups
const fetchExistingGroups = async () => {
  try {
    loadingGroups.value = true;
    const token = localStorage.getItem("adminToken");
    const response = await axios.get(`${API_BASE_URL}/seller-groups`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data.success) {
      existingGroups.value = response.data.sellerGroups || [];
    }
  } catch (error) {
    console.error("Error fetching groups:", error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to load existing groups",
      life: 3000,
    });
  } finally {
    loadingGroups.value = false;
  }
};

// Handle Next button
const handleNext = async () => {
  submitted.value = true;

  // Prepare validation data
  const validationData = {
    mode: groupMode.value,
    selectedGroupId: selectedGroup.value?.id,
    ...form.value,
  };

  // Run validation with data
  const isValid = validate(validationData);

  if (!isValid) {
    // Show first validation error
    const firstError = Object.values(errors)[0];
    toast.add({
      severity: "warn",
      summary: "Validation Error",
      detail: firstError || "Please fill in all required fields correctly",
      life: 3000,
    });
    return;
  }

  if (groupMode.value === "new") {
    // Validate partner_id for new groups
    if (!selectedPartnerId.value) {
      toast.add({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select a Partner",
        life: 3000,
      });
      return;
    }

    // Just pass group data to next step (don't create yet)
    emit("next", {
      groupId: null, // Will be created in Step 3
      groupData: {
        ...form.value,
        partner_id: selectedPartnerId.value,
      },
      groupMode: groupMode.value,
    });
  } else {
    // Use existing group (partner_id comes from existing group data)
    if (!selectedGroup.value || !selectedGroup.value.id) {
      toast.add({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select a group",
        life: 3000,
      });
      return;
    }

    emit("next", {
      groupId: selectedGroup.value.id,
      groupData: selectedGroup.value,
      groupMode: groupMode.value,
    });
  }
};

onMounted(() => {
  fetchPartners();
  fetchExistingGroups();
  hydrateFromProps();
});

watch(
  () => props.data,
  () => {
    hydrateFromProps();
  },
  { deep: true }
);

watch(existingGroups, (groups) => {
  if (groupMode.value !== "existing" || !props.data?.id) return;
  const match = groups.find((group) => group.id === props.data.id);
  if (match) {
    selectedGroup.value = match;
  }
});
</script>

<style scoped>
.p-card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
